import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import s from './AppLayout.module.css';

/* -------------------------------------------------------------------------- */
/*  Nav items configuration                                                    */
/* -------------------------------------------------------------------------- */

const NAV_ITEMS: readonly { to: string; label: string; icon: string; end?: boolean }[] = [
  { to: '/app', label: 'Dashboard', icon: '~', end: true },
  { to: '/app/domains', label: 'Domains', icon: '@' },
  { to: '/app/dns', label: 'DNS Status', icon: '#' },
  { to: '/app/audit', label: 'Audit Log', icon: '>' },
  { to: '/app/settings', label: 'Settings', icon: '*' },
];

/* -------------------------------------------------------------------------- */
/*  AppLayout                                                                  */
/* -------------------------------------------------------------------------- */

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  // Simple usage — placeholder values until API data is loaded by pages
  const reportsUsed = 82;
  const reportsLimit = 100;
  const usagePct = Math.min((reportsUsed / reportsLimit) * 100, 100);
  const isOverLimit = reportsUsed >= reportsLimit;

  return (
    <div className={s.layout}>
      {/* Sidebar */}
      <aside className={s.sidebar}>
        <div className={s.sidebarInner}>
          {/* Logo */}
          <div className={s.logo}>DMARCopolo</div>

          {/* Navigation */}
          <nav className={s.nav}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  isActive ? s.navItemActive : s.navItem
                }
              >
                <span className={s.navIcon}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className={s.sidebarFooter}>
            {/* Usage bar */}
            <div className={s.usageSection}>
              <div className={s.usageLabel}>
                <span>Reports</span>
                <span>{reportsUsed}/{reportsLimit}</span>
              </div>
              <div className={s.usageBarTrack}>
                <div
                  className={
                    isOverLimit ? s.usageBarFillWarning : s.usageBarFill
                  }
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            </div>

            {/* User + logout */}
            <div className={s.userSection}>
              <span className={s.userEmail}>{user?.email ?? '—'}</span>
              <button
                type="button"
                className={s.logoutBtn}
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className={s.content}>
        <div className={s.contentInner}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
